import { Abas } from '@core/components/Abas';
import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { mensagemCampoObrigatorio, mensagemMinimo3Caracteres } from '@core/util/mensagemUtil';
import { cancelarAlert } from 'app/services/alerts';
import { validateCnpj } from 'app/utils/validation';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FaShippingFast } from 'react-icons/fa';
import * as yup from 'yup';
import { TransportadoraAction } from '../../store/transportadoraAction';
import { useTransportadora } from '../../store/transportadoraContext';
import ContatoTab from './ContatoTab';
import DadosGeraisTab from './DadosGeraisTab';
import EnderecoTab from './EnderecoTab';

function TransportadoraCadastroModal() {
  const [selectedTab, setSelectedTab] = useState(0);

  const [state, dispatch] = useTransportadora();

  const schemaPessoaJuridica = yup
    .object()
    .shape({
      cnpj: yup.string().test('test cnpj', 'CNPJ inválido', (value) => {
        if (value) {
          const validate = validateCnpj(value);
          return validate;
        }
        return true;
      }),
      inscricaoEstadual: yup
        .string()
        .nullable()
        .min(3, mensagemMinimo3Caracteres),
    })
    .nullable();

  const schemaContatos = yup.array().of(
    yup.object().shape({
      nomeReferencia: yup
        .string()
        .nullable()
        .min(3, mensagemMinimo3Caracteres),
      email: yup.string().email('E-mail inválido'),
      emailsAdicionais: yup.array().of(
        yup.object().shape({
          email: yup.string().email('E-mail inválido'),
        })
      ),
    })
  );

  const schema = yup.object().shape({
    pessoa: yup
      .object()
      .shape({
        nome: yup
          .string()
          .required(mensagemCampoObrigatorio('o nome'))
          .min(3, mensagemMinimo3Caracteres),
        inscricaoMunicipal: yup
          .string()
          .nullable()
          .min(3, mensagemMinimo3Caracteres),
        pessoaJuridica: schemaPessoaJuridica,
        contatos: schemaContatos,
      })
      .required(),
  });

  const formik = useFormik({
    initialValues: state.registro,
    validationSchema: schema,
    validationOnChange: false,
  });

  useEffect(() => {
    if (state.cadastrar) {
      setSelectedTab(0);
      formik.resetForm({
        values: {
          ...state.registro,
        },
      });
    }
    // Efeito reativo apenas ao gatilho acima; incluir as demais reiniciaria o formulário durante a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cadastrar, state.registro.id]);

  const handleCancelar = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: async () => {
          dispatch(TransportadoraAction.fecharModalCadastro());
          formik.resetForm({ values: {} });
        },
      });
    } else {
      dispatch(TransportadoraAction.fecharModalCadastro());
      formik.resetForm({ values: {} });
    }
  };

  const handleSalvar = () => {
    formik.setFieldTouched('pessoa.nome', true, true);

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        if (formik.values && formik.values.id) {
          TransportadoraAction.atualizarRegistro(dispatch, formik.values, state.requestParams);
        } else {
          TransportadoraAction.criarRegistro(dispatch, formik.values, state.requestParams);
        }
      } else if (formik.errors?.pessoa?.nome) {
        setSelectedTab(0);
      } else if (formik.errors?.pessoa?.contatos?.length) {
        setSelectedTab(2);
      }
    });
  };

  const handleFetchPapel = (transportadoraId) => {
    TransportadoraAction.editarRegistro(dispatch, { id: transportadoraId });
  };

  return (
    <FormikProvider value={formik}>
      <SimpleDialog fullScreen open={state.cadastrar} onClose={handleCancelar}>
        <FormDialogContent
          fullScreen
          onSubmit={handleSalvar}
          header={{
            title: 'Cadastro de transportadora',
            icon: <FaShippingFast />,
            toolbar: (
              <>
                <CancelButton onClick={handleCancelar}>Cancelar</CancelButton>
                <SaveButton>Salvar</SaveButton>
              </>
            ),
          }}
          contentToolbar={
            <Abas
              value={selectedTab}
              onChange={setSelectedTab}
              tabs={[
                {
                  label: 'Dados gerais',
                  hasError: () => {
                    return formik.errors?.pessoa?.nome && formik.touched?.pessoa?.nome;
                  },
                },
                { label: 'Endereço' },
                {
                  label: 'Contato',
                  hasError: () => {
                    return formik.errors?.pessoa?.contatos?.some((contato) => {
                      return contato.email || contato?.emailsAdicionais?.some((adicional) => adicional.email);
                    });
                  },
                },
              ]}
            />
          }
          content={
            <>
              {selectedTab === 0 && <DadosGeraisTab papel="transportadora" fetchPapel={handleFetchPapel} />}
              {selectedTab === 1 && <EnderecoTab />}
              {selectedTab === 2 && <ContatoTab />}
            </>
          }
        />
      </SimpleDialog>
    </FormikProvider>
  );
}

export default TransportadoraCadastroModal;
